/**
 * Proposition d'une clé d'accès après une connexion sans elle (24/10/2026,
 * issue #138), comme les grands services : un conseil fermable sur l'accueil,
 * « Activer » en un appui (Face ID, empreinte, Windows Hello…). Fermé, il se
 * tait 30 jours sur cet appareil. Rien pour un navigateur sans clés d'accès,
 * un compte qui en a déjà, ni une session d'assistance.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { passkeyCancelled, passkeyErrorMessage, passkeyMethodName, platformPasskeyAvailable, registerPasskey } from '../../core/passkeys.js';
import { rememberAccount } from '../../core/remembered-account.js';
import { Banner } from '../../components/ui/Feedback.js';
import { describeDevice } from './AccountDevices.js';
import { PASSKEYS_QUERY, usePasskeys } from './use-passkeys.js';

const KEY = 'fa.offreCle';
const QUIET_MS = 30 * 24 * 3600 * 1000;

function dismissedRecently(userId: string): boolean {
  try {
    const at = Number(JSON.parse(localStorage.getItem(KEY) ?? '{}')[userId] ?? 0);
    return Date.now() - at < QUIET_MS;
  } catch {
    return false;
  }
}

function dismiss(userId: string): void {
  try {
    const all = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, number>;
    localStorage.setItem(KEY, JSON.stringify({ ...all, [userId]: Date.now() }));
  } catch {
    /* stockage refusé : le conseil reviendra, sans gêner */
  }
}

/** La proposition est-elle à faire ? Sert aussi à l'accueil pour n'afficher qu'un conseil. */
export function usePasskeyOffer(): { visible: boolean; hide: () => void; added: () => void } {
  const { me } = useAuth();
  // Seulement avec un lecteur intégré : sinon « Activer » ouvrirait une demande de clé physique.
  const [supported, setSupported] = useState(false);
  useEffect(() => { void platformPasskeyAvailable().then(setSupported); }, []);
  const [hidden, setHidden] = useState(false);
  // Clé tout juste ajoutée : le conseil reste le temps de le dire.
  const [justAdded, setJustAdded] = useState(false);
  const eligible = Boolean(me && !me.support_session && supported && !hidden && !dismissedRecently(me.id));
  const passkeys = usePasskeys(eligible);
  return {
    visible: eligible && (justAdded || (passkeys.data !== undefined && passkeys.data.items.length === 0)),
    hide: () => {
      if (me) dismiss(me.id);
      setHidden(true);
    },
    added: () => setJustAdded(true),
  };
}

export function PasskeyOffer(props: { onDone: () => void; onAdded: () => void }): ReactNode {
  const queryClient = useQueryClient();
  const add = useMutation({
    mutationFn: () => registerPasskey(describeDevice(navigator.userAgent)),
    onSuccess: async () => {
      props.onAdded();
      rememberAccount({ passkey: true });
      await queryClient.invalidateQueries({ queryKey: PASSKEYS_QUERY });
    },
  });
  if (add.isSuccess) {
    return <Banner onDismiss={props.onDone}>{t('compte.clesAcces.ajoutee', { nom: add.data.label ?? t('compte.clesAcces.sansNom') })}</Banner>;
  }
  const error = add.error && !passkeyCancelled(add.error) ? passkeyErrorMessage(add.error) : undefined;
  return (
    <Banner tone={error ? 'danger' : 'info'} title={t('compte.clesAcces.offreTitre')}
      action={{ label: add.isPending ? t('compte.clesAcces.ajoutEnCours') : t('compte.clesAcces.offreActiver'), onClick: () => add.mutate() }}
      onDismiss={props.onDone}>
      {error ?? t('compte.clesAcces.offre', { methode: passkeyMethodName(true) })}
    </Banner>
  );
}
